import { Button, TreeSelect, App, Typography } from 'antd';
import { useMemo } from 'react';
import BenefitRow from './BenefitRow';
import { filterTreeNodeByTitle } from '@/utils/antdTreeSelect';
import { applyForumHallTreeDisabled } from '../utils/forumHallTree';

const { Text } = Typography;

/**
 * 论坛类权益区块 - 支持多论坛管理，每个论坛独立选择权益
 * @param {Object} props
 * @param {Array} props.forums 论坛列表 [{ hallsId, hallName, benefits: [] }]
 * @param {Array} props.hallTreeData 论坛 TreeSelect 树形数据
 * @param {Object} props.hallNameMap hallsId → 会场名称映射
 * @param {Function} props.onAddForum 添加论坛回调
 * @param {Function} props.onRemoveForum 移除论坛回调 (index)
 * @param {Function} props.onForumChange 论坛选择变更回调 (index, hallsId, hallName)
 * @param {Function} props.onSelectBenefit 选择权益按钮回调 (forumIndex)
 * @param {Function} props.onBenefitChange 权益行数据变化 (forumIndex, conferenceBenefitId, updatedBenefit)
 * @param {Function} props.onRemoveBenefit 移除权益 (forumIndex, conferenceBenefitId)
 * @param {Object}  [props.benefitErrors] 校验错误 { [forumIndex]: { hallsId?: string, benefits?: string, [conferenceBenefitId]: { quantity?, boothNumbers? } } }
 * @param {boolean} [props.isEditMode]   是否为编辑模式（透传给 BenefitRow）
 */
export default function ForumBenefitSection({ forums = [], hallTreeData = [], hallNameMap = {}, onAddForum, onRemoveForum, onForumChange, onSelectBenefit, onBenefitChange, onRemoveBenefit, benefitErrors = {}, isEditMode = false }) {
    const { modal } = App.useApp();

    /**
     * 已被其他论坛块占用的 hallsId 集合，用于禁用重复选项。
     * 依赖 forums 数组，每次渲染时重新计算。
     */
    const usedHallIds = useMemo(() => new Set(forums.map(f => f.hallsId).filter(Boolean)), [forums]);

    /**
     * 为指定论坛块生成带 disabled 状态的树形数据
     */
    const getForumTreeData = index => {
        const currentHallsId = forums[index]?.hallsId;
        return applyForumHallTreeDisabled(hallTreeData, usedHallIds, currentHallsId);
    };

    /**
     * 移除论坛前无论是否有数据，都弹出二次确认。
     */
    const handleRemoveForum = index => {
        modal.confirm({
            title: '提示',
            content: '移除后无法恢复数据，确认移除该论坛。',
            okText: '确认',
            cancelText: '取消',
            centered: true,
            onOk: () => onRemoveForum(index),
        });
    };

    return (
        <div className="benefit-category-section">
            <div className="benefit-category-header">
                <span className="benefit-category-title">
                    权益分类：论坛
                    {forums.length > 0 && (
                        <Text type="secondary" style={{ marginLeft: 4 }}>
                            （{forums.reduce((sum, f) => sum + (f.benefits?.length || 0), 0)}项）
                        </Text>
                    )}
                </span>
            </div>
            <div className="benefit-category-list">
                {forums.length > 0 ? (
                    forums.map((forum, index) => {
                        const blockErrors = benefitErrors[index] || {};
                        const hallsIdError = typeof blockErrors.hallsId === 'string' ? blockErrors.hallsId : null;
                        const benefitsError = typeof blockErrors.benefits === 'string' ? blockErrors.benefits : null;

                        return (
                            <div key={forum._key || index} className="forum-block">
                                <div className="forum-block-header">
                                    <div className="forum-block-select">
                                        <label className="benefit-field-label">
                                            <span className="benefit-required-mark">*</span>
                                            论坛：
                                        </label>
                                        <div className="forum-block-select-control">
                                            <TreeSelect
                                                placeholder="请选择论坛"
                                                value={forum.hallsId || undefined}
                                                onChange={val => onForumChange(index, val, hallNameMap[val] || '')}
                                                treeData={getForumTreeData(index)}
                                                showSearch={{ filterTreeNode: filterTreeNodeByTitle }}
                                                styles={{ popup: { root: { minWidth: 500, overflow: 'auto' } } }}
                                                allowClear
                                                treeDefaultExpandAll
                                                status={hallsIdError ? 'error' : undefined}
                                                style={{ width: '100%' }}
                                            />
                                            {hallsIdError && <div className="benefit-field-error">{hallsIdError}</div>}
                                        </div>
                                    </div>
                                    <Button type="link" danger size="small" onClick={() => handleRemoveForum(index)}>
                                        移除论坛
                                    </Button>
                                </div>
                                <div className="forum-block-benefits">
                                    {forum.benefits && forum.benefits.length > 0 ? (
                                        forum.benefits.map(b => (
                                            <BenefitRow key={b.conferenceBenefitId} benefit={b} onChange={updated => onBenefitChange(index, b.conferenceBenefitId, updated)} onRemove={() => onRemoveBenefit(index, b.conferenceBenefitId)} isEditMode={isEditMode} errors={blockErrors[b.conferenceBenefitId] || {}} />
                                        ))
                                    ) : (
                                        <div className={`benefit-category-empty${benefitsError ? ' benefit-category-empty-error' : ''}`}>
                                            <Text type={benefitsError ? 'danger' : 'secondary'}>{benefitsError || '暂未选择权益'}</Text>
                                        </div>
                                    )}
                                    <Button type="dashed" block className="benefit-add-btn" onClick={() => onSelectBenefit(index)}>
                                        <i className="bi bi-plus-circle" style={{ marginRight: 4 }} />
                                        添加权益
                                    </Button>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="benefit-category-empty">
                        <Text type="secondary">暂未添加论坛</Text>
                    </div>
                )}
                <Button type="dashed" block className="benefit-add-btn" onClick={onAddForum}>
                    <i className="bi bi-plus-circle" style={{ marginRight: 4 }} />
                    添加论坛
                </Button>
            </div>
        </div>
    );
}
